/**
 * Keeps one screen's render error from blanking the whole control plane.
 * T29 / NX-23 graceful-failure (S18).
 *
 * WHY. The Admin had no error boundary anywhere. A render-time exception in
 * any panel — an unexpected response shape during a partial outage, a field a
 * newer backend renamed — made React 19 unmount the entire root: a white
 * screen, header and sidebar gone, no message, and no way back but the
 * browser's reload. Measured in a real browser on the tenant detail page's
 * Provisioning tab (S18 evidence).
 *
 * WHAT IT DOES. Wraps the routed page inside AdminLayout's <main>, so the
 * shell and its navigation survive. The failure is said inline (role="alert",
 * no pop-up), with "Try again" (re-render this screen) and "Reload page".
 * `resetKey` is the pathname: navigating to another screen clears it.
 *
 * WHAT IT DOES NOT DO. It does not claim anything about data. A render error
 * cannot itself write anything, but an action the operator took before it may
 * or may not have completed — so the copy does not say "nothing changed".
 * It does not catch errors in event handlers or async code (React boundaries
 * never do); those stay with each screen's own error handling.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/button';

export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, attempt: 0 };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Kept for diagnosis in the browser console; never shown to the operator.
    // eslint-disable-next-line no-console
    console.error('Admin screen failed to render', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  retry() {
    this.setState((s) => ({ error: null, attempt: s.attempt + 1 }));
  }

  render() {
    const { error, attempt } = this.state;
    if (!error) {
      // `attempt` remounts the subtree on "Try again" so it starts clean.
      return <React.Fragment key={attempt}>{this.props.children}</React.Fragment>;
    }
    return (
      <div className="mx-auto max-w-2xl p-6">
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4"
          data-testid="admin-route-error"
        >
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-destructive-text" />
          <div className="min-w-0 space-y-3">
            <div>
              <p className="font-medium text-foreground">This screen could not be displayed.</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Something in it failed while drawing. The navigation and every other screen still work.
                Try again, or reload the page; if it keeps happening, note the time and the screen
                for support.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={this.retry}>Try again</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
